import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';
import { formatDateBr } from '@core/utils/filter';
import SISComunicadoService from '@/services/Cadastros/Comunicado/SISComunicado.service';
// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default function useUsersList(nameModule) {
  // Use toast
  const toast = useToast();
  const sISComunicadoService = new SISComunicadoService();
  const refComunicadoListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: 'comunicadotitulo_ptbr', label: 'Título', sortable: true },
    { key: 'comunicadoconteudo_ptbr', label: 'Conteúdo', sortable: true },
    { key: 'actions', label: 'Ações', sortable: false },
  ];
  const perPage = ref(10);
  const totalComunicado = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('comunicadotitulo_ptbr');
  const isSortDirDesc = ref(false);
  const overlay = ref(true);
  const statusFilter = ref(null);
  const comunicadoPayload = body => ({
    ...(body.comunicadoid ? { comunicadoid: body.comunicadoid } : {}),
    comunicadotitulo_ptbr: body.comunicadotitulo_ptbr,
    comunicadoconteudo_ptbr: body.comunicadoconteudo_ptbr,
    destinatarioTipo: body.destinatarioTipo,
    representanteIds: Array.isArray(body.representanteIds) ? body.representanteIds : [],
  });

  const dataMeta = computed(() => {
    const localItemsCount = refComunicadoListTable.value ? refComunicadoListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalComunicado.value,
    };
  });

  const refetchData = () => {
    refComunicadoListTable.value.refresh();
  };

  watch([currentPage, perPage, searchQuery, statusFilter], () => {
    refetchData();
  });

  const fetchComunicado = (ctx, callback) => {
    overlay.value = true;
    sISComunicadoService.index({
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      fullObject: true,
    }).then((response) => {
      const { data, total } = response;
      overlay.value = false;
      callback(data);
      perPage.value = response.perPage;
      currentPage.value = response.page;
      totalComunicado.value = total;
    })
      .catch((error) => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching users list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  const create = async (body) => {
    try {
      const response = await sISComunicadoService.store(comunicadoPayload(body));

      if (response) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            text: 'Comunicado cadastrado com sucesso!',
            icon: 'CheckIcon',
            variant: 'success',
          },
        });
        refetchData();
        return true;
      } else {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Comunicado não foi cadastrado.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
        return false;
      }
    } catch (error) {
      console.log('ERRO: ', error);
      toast({
        component: ToastificationContent,
        props: {
          title: 'Erro',
          text: `${error}`,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      });
      return false;
    }
  };
  const Updated = async (body) => {
    try {
      await sISComunicadoService.update(comunicadoPayload(body));
      toast({
        component: ToastificationContent,
        props: {
          title: 'Sucesso',
          text: 'Comunicado atualizado com sucesso!',
          icon: 'CheckIcon',
          variant: 'success',
        },
      });
      refetchData();
      return true;
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Erro',
          text: error.response?.data?.message || String(error),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      });
      return false;
    }
  };

  const removerData = async (id) => {
    try {
      await sISComunicadoService.destroy(id);
      refetchData();
      return true;
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Erro',
          text: error.response?.data?.message || String(error),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      });
      return false;
    }
  };

  const resolveTypeFieldText = type => {
    if (type === 'VARCHAR') return 'Texto';
    if (type === 'BOOLEAN') return 'Verdadeiro/Falso';
    if (type === 'DATE') return 'Data';
    if (type === 'DATETIME') return 'Data e Hora';
    if (type === 'DECIMAL') return 'Monetário';
    if (type === 'TEXT') return 'Texto Longo';
    if (type === 'INTEGER' || type === 'INT') return 'Númerico';
    return 'Sem ';
  };
  const resolveTypeFieldIcon = type => {
    if (type === 'VARCHAR') return 'tabler:abc';
    if (type === 'BOOLEAN') return 'Verdadeiro/Falso';
    if (type === 'DATE') return 'system-uicons:calendar-date';
    if (type === 'DATETIME') return 'formkit:datetime';
    if (type === 'TEXT') return 'mdi:text-long';
    if (type === 'DECIMAL') return 'tabler:decimal';
    if (type === 'DECIMAL' || type === 'INTEGER' || type === 'INT') return 'tabler:123';
    return 'nonicons:type-16';
  };

  const resolveItemStatusVariant = (status) => {
    if (status === 'PENDING') return 'light-warning';
    if (status === 'ATIVO') return 'light-success';
    if (status === 'INATIVO') return 'light-danger';
    return 'primary';
  };

  return {
    fetchComunicado,
    tableColumns,
    perPage,
    currentPage,
    totalComunicado,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refComunicadoListTable,
    overlay,
    resolveTypeFieldText,
    resolveTypeFieldIcon,
    resolveItemStatusVariant,
    refetchData,
    create,
    Updated,

    // Extra Filters
    statusFilter,
    removerData,
  };
}
